'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  totp_enabled?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  sessionExp: number | null;
  signIn: (email: string, password: string) => Promise<{ requires2fa: boolean; pendingToken?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  sessionExp: null,
  signIn: async () => ({ requires2fa: false }),
  signOut: async () => {},
  refreshSession: async () => {},
  reloadUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExp, setSessionExp] = useState<number | null>(null);
  const router = useRouter();

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me');
      const { user: u, exp } = await res.json();
      setUser(u ?? null);
      setSessionExp(exp ?? null);
    } catch {
      setUser(null);
      setSessionExp(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const signIn = async (email: string, password: string): Promise<{ requires2fa: boolean; pendingToken?: string }> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');

    if (data.requires2fa) {
      return { requires2fa: true, pendingToken: data.pendingToken };
    }

    setUser(data.user);
    setSessionExp(Math.floor(Date.now() / 1000) + 30 * 60);
    router.push('/overview-dashboard');
    router.refresh();
    return { requires2fa: false };
  };

  const signOut = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setSessionExp(null);
    router.push('/login');
    router.refresh();
  };

  const refreshSession = async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST' });
      if (res.ok) {
        const { exp } = await res.json();
        setSessionExp(exp ?? null);
      }
    } catch {
      // silent
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, sessionExp, signIn, signOut, refreshSession, reloadUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}
